import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import dormakabaDashboardIcon from '../../assets/dormakaba-dashboard-icon.svg';
import mvideoSvg from '../../assets/mvideo.svg';
import CustomDate from '../CustomDate';
import messages from './messages';
import MobileVideoCardStatus from './MobileVideoCardStatus';
import PaidServiceChip from './PaidServiceChip';

const styles = {
  chip: {
    width: 'fit-content',
  },
  icon: {
    height: 48,
    width: 48,
  },
  imageWrapper: {
    '& img': {
      height: '48px',
      width: '48px',
    },
    mr: 2,
  },
  paidRoot: {
    height: '100%',
    width: '100%',
  },
  root: {
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  serviceInfo: {
    minWidth: 0,
  },
  serviceRow: {
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 2,
  },
};

const StatusInfo = ({
  devices,
  electronicLockDevices,
  electronicLockService,
  isDormakabaActive = false,
  licenceModel,
  mvService,
}) => {
  const products = [
    {
      alt: '2N Mobile Video',
      devices,
      icon: mvideoSvg,
      licenceModel,
      service: mvService,
      testId: 'paid-service-mobile-video',
      tooltip: messages.mobileVideoCardMobileVideoTooltip,
    },
    {
      alt: 'Dormakaba',
      devices: electronicLockDevices,
      icon: dormakabaDashboardIcon,
      licenceModel: electronicLockService.licenceModel || {},
      service: electronicLockService,
      testId: 'paid-service-dormakaba',
      tooltip: messages.mobileVideoCardDormakabaTooltip,
    },
  ];

  return isDormakabaActive ? (
    <Grid container direction="column" spacing={4} sx={styles.paidRoot}>
      {products.map((product) => (
        <Grid container data-testid={product.testId} key={product.alt} sx={styles.serviceRow}>
          <Tooltip title={<FormattedMessage {...product.tooltip} />}>
            <img alt={product.alt} src={product.icon} style={styles.icon} />
          </Tooltip>
          <Grid sx={styles.serviceInfo}>
            <Typography sx={{ fontWeight: 'bold' }}>
              <FormattedMessage
                {...messages.mobileVideoCardActiveUntil}
                values={{
                  date: product.licenceModel.validityTo ? (
                    <CustomDate dateValue={product.licenceModel.validityTo} showTime showYear />
                  ) : (
                    '-'
                  ),
                  span: (chunks) => (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {chunks}
                    </Box>
                  ),
                  status: (chunks) => (
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      {chunks}
                    </Box>
                  ),
                }}
              />
            </Typography>
            <Box sx={styles.chip}>
              <PaidServiceChip
                devices={product.devices}
                isDormakabaActive={isDormakabaActive}
                licenceModel={product.licenceModel}
                mvService={product.service}
              />
            </Box>
          </Grid>
        </Grid>
      ))}
    </Grid>
  ) : (
    <Grid container sx={styles.root}>
      <Grid sx={styles.imageWrapper}>
        <img alt="2N Mobile Video" src={mvideoSvg} />
      </Grid>
      <Grid sx={{ mb: { sm: 'inherit', xs: 2 } }}>
        <Typography sx={{ lineHeight: 1.43, mb: { sm: 1, xs: 'inherit' } }} variant="h6">
          <MobileVideoCardStatus devices={devices} licenceModel={licenceModel} mvService={mvService} />
        </Typography>
      </Grid>
    </Grid>
  );
};
StatusInfo.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
  electronicLockDevices: PropTypes.arrayOf(PropTypes.object).isRequired,
  electronicLockService: PropTypes.shape({
    licenceModel: PropTypes.shape({
      additional: PropTypes.number,
      licencesPool: PropTypes.number,
      status: PropTypes.string,
      unlicensedCount: PropTypes.number,
      validityTo: PropTypes.string,
    }),
    subscribed: PropTypes.bool,
  }).isRequired,
  isDormakabaActive: PropTypes.bool,
  licenceModel: PropTypes.shape({
    additional: PropTypes.number,
    licencesPool: PropTypes.number,
    status: PropTypes.string,
    unlicensedCount: PropTypes.number,
    validityTo: PropTypes.string,
  }).isRequired,
  mvService: PropTypes.shape({
    subscribed: PropTypes.bool,
  }).isRequired,
};

export default StatusInfo;
